import React, { useEffect, useState } from 'react';
import { Clock, Play, AlertCircle, Zap } from 'lucide-react';
import { api } from '../../../lib/api';
import { useAuth } from '../../../context/AuthContext';
import EmptyState from '../../../components/crm/EmptyState';

// /admin/system/crons — Sprint H item 9.8.
// Read-only schedule + manual trigger. Admin + ops (per migration 027).
// Also the switch for the automatic batch dispatch sweep (admins only;
// docs/auto-resolve-batch.md §12): off | shadow | on, plus its last run.

const SWEEP_MODES = [
  { id: 'off', label: 'Off', hint: 'Nothing is dispatched automatically.' },
  { id: 'shadow', label: 'Shadow', hint: 'Every 15 minutes it records what it would dispatch, and dispatches nothing.' },
  { id: 'on', label: 'On', hint: 'Dispatches one batch per quiet school; drafts still wait for your review and Merge & publish.' },
];

function SweepPanel({ sweep, isAdmin, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  if (!sweep) return null;
  const last = sweep.last;
  const setMode = async (mode) => {
    if (busy || mode === sweep.mode) return;
    if (mode === 'on' && !window.confirm('Turn automatic dispatch ON? Every 15 minutes, schools whose open edit requests have gone quiet get one batch dispatched. Drafts still need your Merge & publish.')) return;
    setBusy(true); setErr('');
    try { await api.put('/api/admin/settings/auto_resolve_sweep', { value: mode }); await onChanged(); }
    catch (e) { setErr(e?.data?.error || e?.message || 'Could not change the switch'); }
    finally { setBusy(false); }
  };
  const summarize = () => {
    if (!last) return 'No run recorded yet.';
    const parts = [];
    parts.push(`${last.considered ?? 0} school${last.considered === 1 ? '' : 's'} with eligible requests`);
    const d = Array.isArray(last.dispatched) ? last.dispatched : [];
    if (d.length) parts.push(`${last.mode === 'shadow' ? 'would dispatch' : 'dispatched'} ${d.map((x) => `${x.slug} (${x.requestIds?.length ?? x.count})`).join(', ')}`);
    const w = Array.isArray(last.waiting) ? last.waiting : [];
    if (w.length) parts.push(`${w.length} waiting on the clock`);
    const s = Array.isArray(last.skipped) ? last.skipped : [];
    if (s.length) parts.push(`${s.length} skipped (${[...new Set(s.map((x) => x.reason))].join(', ')})`);
    const f = Array.isArray(last.failed) ? last.failed : [];
    if (f.length) parts.push(`${f.length} failed`);
    return parts.join(' · ');
  };
  return (
    <div data-testid="sweep-panel" className="mb-6 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-white text-[14px] font-semibold">
            <Zap className="w-4 h-4 text-[#B0FC38]" /> Automatic batch dispatch
            <span className="text-[10px] uppercase tracking-wider text-gray-500">every 15 min</span>
          </div>
          <p className="text-[12px] text-gray-500 mt-1 max-w-2xl">
            A school is due once its oldest open edit request is {sweep.limits?.minAgeMinutes ?? 60} minutes old and nothing new has arrived for {sweep.limits?.quietMinutes ?? 45} minutes,
            or after {sweep.limits?.maxWaitHours ?? 4} hours regardless. At most {sweep.limits?.dailyFleetCap ?? 15} dispatches a day, {sweep.limits?.dailySchoolCap ?? 3} per school, {sweep.limits?.maxInFlight ?? 4} in flight.
            Nothing is emailed; the resolved email after you publish stays the only one.
          </p>
          {!sweep.flagOn && (
            <p className="text-[12px] text-amber-300 mt-1">Disabled in wrangler.toml (AUTO_RESOLVE_SWEEP_ENABLED) — the switch below has no effect until that is "true".</p>
          )}
        </div>
        <div className="inline-flex rounded-lg border border-white/[0.08] overflow-hidden" role="group" aria-label="Automatic dispatch mode">
          {SWEEP_MODES.map((m) => (
            <button
              key={m.id}
              type="button"
              data-testid={`sweep-mode-${m.id}`}
              title={m.hint}
              disabled={!isAdmin || busy}
              aria-pressed={sweep.mode === m.id}
              onClick={() => setMode(m.id)}
              className={`px-3 py-1.5 text-[12px] ${sweep.mode === m.id ? 'bg-[#B0FC38]/20 text-white' : 'text-gray-400 hover:bg-white/[0.04]'} disabled:opacity-60`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>
      <div className="mt-3 text-[12px] text-gray-400" data-testid="sweep-last">
        <span className="text-gray-500">Last check:</span> {last?.at ? fmtRelative(last.at) : 'never'}
        {last?.mode && <span className="ml-1 text-gray-500">({last.mode})</span>}
        <span className="mx-2 text-gray-600">·</span>{summarize()}
      </div>
      {!isAdmin && <div className="mt-1 text-[11px] text-gray-500">Only admins can change the switch.</div>}
      {err && <div className="mt-2 text-[12px] text-red-300/90">{err}</div>}
    </div>
  );
}

function fmtRelative(iso) {
  if (!iso) return 'never';
  const d = new Date(iso);
  if (Number.isNaN(d.valueOf())) return iso;
  const ms = d.getTime() - Date.now();
  const abs = Math.abs(ms);
  const m = Math.floor(abs / 60000);
  const h = Math.floor(m / 60);
  const days = Math.floor(h / 24);
  const future = ms > 0;
  if (days > 1) return future ? `in ${days}d` : `${days}d ago`;
  if (h > 1) return future ? `in ${h}h` : `${h}h ago`;
  if (m > 0) return future ? `in ${m}m` : `${m}m ago`;
  return future ? 'imminent' : 'just now';
}

export default function Crons() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [running, setRunning] = useState(null);
  const [results, setResults] = useState({});

  const load = React.useCallback(() => {
    api.get('/api/admin/system/crons')
      .then(setData)
      .catch((err) => setError(err?.data?.error || err?.message || ''));
  }, []);
  useEffect(() => { load(); }, [load]);

  const trigger = async (cronId) => {
    if (!window.confirm(`Manually trigger ${cronId}? The handler will run synchronously and log to activity_log.`)) return;
    setRunning(cronId);
    try {
      const r = await api.post('/api/admin/system/crons/trigger', { cron_id: cronId });
      setResults((prev) => ({ ...prev, [cronId]: r }));
      load();
    } catch (err) {
      setResults((prev) => ({ ...prev, [cronId]: { error: err?.data?.error || err?.message || 'failed' } }));
    } finally {
      setRunning(null);
    }
  };

  if (user?.role !== 'nexsav_admin' && user?.role !== 'ops') {
    return <EmptyState icon={AlertCircle} title="Staff only" description="Cron management is staff-only." />;
  }

  return (
    <div data-testid="crons-page" className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-xl md:text-2xl font-semibold text-white tracking-tight">Cron <span className="serif-acc accent-text">schedule</span></h1>
        <p className="text-[13px] text-gray-500 mt-0.5">
          Read-only schedule + manual trigger. Triggers are logged in the activity log.
          {data && (
            <span className={`ml-2 ${data.enabled ? 'text-[#B0FC38]' : 'text-amber-300'}`}>
              {data.enabled ? '● enabled' : '● disabled (NEXSAV_CRON_ENABLED != "true")'}
            </span>
          )}
        </p>
      </div>

      {error && <div className="text-[12px] text-red-300/90 mb-3">{error}</div>}

      {data?.sweep && <SweepPanel sweep={data.sweep} isAdmin={user?.role === 'nexsav_admin'} onChanged={async () => { load(); }} />}

      {!data ? <div className="text-[12px] text-gray-500">Loading…</div> : (
        <table className="w-full text-[13px]" data-testid="crons-table">
          <thead className="text-gray-500 uppercase tracking-wider text-[10px]">
            <tr>
              <th className="text-left py-2">ID</th>
              <th className="text-left py-2">Schedule</th>
              <th className="text-left py-2">Description</th>
              <th className="text-left py-2">Next fire</th>
              <th className="text-left py-2">Last manual</th>
              <th className="text-right py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/[0.04]">
            {data.crons.map((c) => (
              <tr key={c.id} data-testid={`cron-row-${c.id}`} className="border-t border-white/[0.04]">
                <td className="py-3 text-white font-mono text-[12px]">{c.id}</td>
                <td className="py-3 text-gray-400 font-mono text-[12px]">{c.cron}</td>
                <td className="py-3 text-gray-300">{c.description}</td>
                <td className="py-3 text-[11px] text-gray-500">
                  <Clock className="inline w-3 h-3 mr-1" />
                  {fmtRelative(c.next_fire_estimate)}
                </td>
                <td className="py-3 text-[11px] text-gray-500">{fmtRelative(c.last_manual_trigger)}</td>
                <td className="py-3 text-right">
                  <button
                    type="button"
                    data-testid={`cron-trigger-${c.id}`}
                    onClick={() => trigger(c.id)}
                    disabled={running === c.id}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-[12px] bg-[#B0FC38]/15 border border-[#B0FC38]/40 text-white hover:bg-[#B0FC38]/25 disabled:opacity-50"
                  >
                    <Play className="w-3 h-3" /> {running === c.id ? 'Running…' : 'Trigger now'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {Object.entries(results).length > 0 && (
        <div className="mt-6 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4">
          <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2">Recent trigger results</div>
          {Object.entries(results).map(([id, r]) => (
            <pre key={id} className="text-[11px] text-gray-300 overflow-x-auto py-1">
              <strong className="text-white">{id}</strong>: {JSON.stringify(r, null, 2)}
            </pre>
          ))}
        </div>
      )}
    </div>
  );
}
